import { jsx as _jsx } from "react/jsx-runtime";
// @ts-nocheck - React Native types are not compatible with React 19
// TODO: Update @types/react-native to be compatible with React 19
import { useRef, useState, useCallback, useEffect } from "react";
import { Modal, View, StyleSheet, TouchableOpacity, Platform, Animated, PanResponder, Dimensions } from "react-native";
import { WebView } from "react-native-webview";
import { SafeAreaView } from "react-native-safe-area-context";
import { IFrameEventType, isOrbyEventTypeKey, OrbyLinkEventType, OAuthPermissionLevel, OrbyLinkOperation, } from "../../utils/types.js";
import { generateUUID, isValidUUID } from "../../utils/crypto.js";
import { extractAccountCluster } from "@orb-labs/orby-core";
let linkState = {
    visible: false,
    url: null,
    options: null,
};
let stateUpdateCallback = null;
function updateState(newState) {
    linkState = { ...linkState, ...newState };
    if (stateUpdateCallback) {
        stateUpdateCallback(linkState);
    }
}
async function handleOrbyEvent(event, options) {
    try {
        const data = JSON.parse(event.nativeEvent.data);
        // Handle events from oauth-backend WebView
        switch (data.type) {
            case IFrameEventType.OpenOAuthPopup: {
                const payload = data.payload;
                const { url } = payload;
                if (url) {
                    // In React Native, we can use Linking to open external browser
                    // or use react-native-inappbrowser-reborn if available
                    const { Linking } = require("react-native");
                    Linking.openURL(url).catch((err) => {
                        console.error("Failed to open OAuth URL:", err);
                    });
                }
                break;
            }
            case IFrameEventType.BrokerageAccountAccessToken: {
                const payload = data.payload;
                options?.onEvent?.({
                    type: OrbyLinkEventType.IntegrationConnected,
                    payload,
                });
                const integrationConnectedEvent = {
                    accountCluster: extractAccountCluster(payload.accountCluster),
                    tokens: payload.tokens,
                };
                options?.onIntegrationConnected?.(integrationConnectedEvent);
                break;
            }
            case IFrameEventType.Done: {
                const payload = data.payload;
                options?.onExit?.(payload?.errorMessage, payload);
                updateState({ visible: false });
                break;
            }
            case IFrameEventType.Close: {
                const payload = data.payload;
                options?.onEvent?.({
                    type: OrbyLinkEventType.Close,
                    payload: data.payload,
                });
                options?.onExit?.(payload?.errorMessage, payload);
                updateState({ visible: false });
                break;
            }
            case IFrameEventType.Loaded: {
                // WebView has loaded - notify the application
                options?.onEvent?.({ type: OrbyLinkEventType.PageLoaded });
                break;
            }
            case IFrameEventType.IntegrationConnectionError: {
                const payload = data.payload;
                options?.onEvent?.({
                    type: OrbyLinkEventType.IntegrationConnectionError,
                    payload: {
                        errorMessage: payload?.errorMessage || "Unknown error occurred",
                    },
                });
                options?.onExit?.(payload?.errorMessage || "Connection error");
                break;
            }
            case IFrameEventType.IntegrationOAuthStarted: {
                options?.onEvent?.({
                    type: OrbyLinkEventType.IntegrationOAuthStarted,
                });
                break;
            }
            default: {
                if (isOrbyEventTypeKey(data.type)) {
                    options?.onEvent?.({
                        type: data.type,
                        payload: data.payload,
                    });
                }
                break;
            }
        }
    }
    catch (error) {
        console.error("[handleOrbyEvent] Failed to parse message:", error);
    }
}
function addLanguage(linkUrl, language) {
    // In React Native, we can use react-native-localize or similar
    // For now, we'll use a simple approach
    if (language === "system") {
        try {
            const { getLocales } = require("react-native-localize");
            const locales = getLocales();
            language = locales?.[0]?.languageTag || "en";
        }
        catch {
            language = "en";
        }
    }
    return `${linkUrl}${linkUrl.includes("?") ? "&" : "?"}lng=${language || "en"}`;
}
const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "flex-end",
    },
    bottomSheet: {
        backgroundColor: "#2A2A2A",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingBottom: 0,
        maxHeight: "90%",
        minHeight: "60%",
    },
    contentContainer: {
        flex: 1,
    },
    instructionText: {
        fontSize: 16,
        color: "#CCCCCC",
        marginBottom: 24,
        fontFamily: Platform.OS === "ios" ? "System" : "sans-serif",
    },
    webview: {
        flex: 1,
        backgroundColor: "#2A2A2A",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
    },
    webviewContainer: {
        flex: 1,
        borderRadius: 12,
        overflow: "hidden",
    },
});
/**
 * React Native OrbyLink Modal Component
 */
export const OrbyLinkModal = () => {
    const [state, setState] = useState(linkState);
    const webViewRef = useRef(null);
    const translateY = useRef(new Animated.Value(0)).current;
    const { height: screenHeight } = Dimensions.get("window");
    useEffect(() => {
        stateUpdateCallback = setState;
        return () => {
            stateUpdateCallback = null;
        };
    }, []);
    const handleClose = useCallback(() => {
        updateState({ visible: false });
        state.options?.onExit?.();
    }, [state.options]);
    // Animate modal when visible changes
    useEffect(() => {
        if (state.visible) {
            Animated.spring(translateY, {
                toValue: 0,
                useNativeDriver: true,
                tension: 65,
                friction: 11,
            }).start();
        }
        else {
            Animated.timing(translateY, {
                toValue: screenHeight,
                duration: 250,
                useNativeDriver: true,
            }).start();
        }
    }, [state.visible, screenHeight]);
    const handleCloseRef = useRef(handleClose);
    useEffect(() => {
        handleCloseRef.current = handleClose;
    }, [handleClose]);
    const panResponder = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_, gestureState) => gestureState.dy > 5,
        onPanResponderMove: (_, gestureState) => {
            if (gestureState.dy > 0) {
                translateY.setValue(gestureState.dy);
            }
        },
        onPanResponderRelease: (_, gestureState) => {
            if (gestureState.dy > 100) {
                handleCloseRef.current();
            }
            else {
                Animated.spring(translateY, {
                    toValue: 0,
                    useNativeDriver: true,
                    tension: 65,
                    friction: 11,
                }).start();
            }
        },
    })).current;
    const handleMessage = useCallback((event) => {
        if (state.options) {
            handleOrbyEvent(event, state.options);
        }
    }, [state.options]);
    if (!state.visible || !state.url) {
        return null;
    }
    return (_jsx(Modal, { visible: state.visible, transparent: true, animationType: "none", onRequestClose: handleClose, children: _jsx(TouchableOpacity, { style: styles.modalOverlay, activeOpacity: 1, onPress: handleClose, children: _jsx(Animated.View, { style: [
                    styles.bottomSheet,
                    {
                        transform: [{ translateY }],
                    },
                ], ...panResponder.panHandlers, children: _jsx(SafeAreaView, { edges: [], style: { flex: 1 }, children: _jsx(View
                    // activeOpacity={1}
                    // onPress={(e) => e.stopPropagation()}
                    , { 
                        // activeOpacity={1}
                        // onPress={(e) => e.stopPropagation()}
                        style: { flex: 1 }, pointerEvents: "box-none", children: _jsx(View, { style: styles.contentContainer, children: _jsx(View, { style: styles.webviewContainer, children: _jsx(WebView, { ref: webViewRef, source: { uri: state.url }, onMessage: (event) => {
                                        try {
                                            const data = JSON.parse(event.nativeEvent.data);
                                            // Handle console log forwarding
                                            if (data.type === "__console_log__") {
                                                return;
                                            }
                                            handleMessage(event);
                                        }
                                        catch (e) {
                                            handleMessage(event);
                                        }
                                    }, style: styles.webview, javaScriptEnabled: true, domStorageEnabled: true, startInLoadingState: true, injectedJavaScriptBeforeContentLoaded: `

                      (function() {
                        // Forward console.log to React Native for debugging
                        const originalLog = console.log;
                        console.log = function(...args) {
                          originalLog.apply(console, args);
                          if (window.ReactNativeWebView) {
                            try {
                              const message = Array.from(args).map(arg => 
                                typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                              ).join(' ');
                              window.ReactNativeWebView.postMessage(JSON.stringify({
                                type: '__console_log__',
                                message: message
                              }));
                            } catch (e) {
                              // Ignore errors
                            }
                          }
                        };
                        
                        // Set up message forwarding from window.postMessage to React Native
                        // This runs before the page content loads
                        window.addEventListener('message', function(event) {
                              console.log('[RN WebView] 000 Message event received:', {
                              data: event.data,
                              origin: event.origin,
                              source: event.source,
                              isObject: typeof event.data === 'object',
                              hasType: event.data && typeof event.data === 'object' && event.data.type
                            });
                            const hasValidData = event.data && typeof event.data === 'object' && event.data.type;
                            if (window.ReactNativeWebView && hasValidData) {
                              // Forward messages that look like Orby events
                              window.ReactNativeWebView.postMessage(JSON.stringify(event.data));
                            }
                          });
                      })();
                      true; // Required for iOS
                    `, injectedJavaScript: `
                      (function() {
                        // Also set up after page loads to catch any messages
                        if (window.ReactNativeWebView) {
                          window.addEventListener('message', function(event) {
                            console.log('[RN WebView] Message event received:', {
                              data: event.data,
                              origin: event.origin,
                              source: event.source,
                              isObject: typeof event.data === 'object',
                              hasType: event.data && typeof event.data === 'object' && event.data.type
                            });
                            
                            if (event.data && typeof event.data === 'object' && event.data.type) {
                              console.log('[RN WebView] Forwarding message to ReactNativeWebView:', event.data);
                              window.ReactNativeWebView.postMessage(JSON.stringify(event.data));
                              console.log('[RN WebView] Message forwarded successfully');
                            } else {
                              console.log('[RN WebView] Message not forwarded - missing type or not an object');
                            }
                          });
                          console.log('[RN WebView] Message event listener set up');
                        } else {
                          console.warn('[RN WebView] ReactNativeWebView not available for message listener');
                        }
                      })();
                      true; // Required for iOS
                    `, 
                                    // Allow navigation to external URLs for OAuth flows
                                    onShouldStartLoadWithRequest: (req) => {
                                        console.log("[] onShouldStartLoadWithRequest", req.url);
                                        // Allow all navigation for now
                                        return true;
                                    } }) }) }) }) }) }) }) }));
};
export const createOrbyLinkReactNative = (options) => {
    if (!options.baseUrl) {
        throw new Error("baseUrl is required in OrbyOptions");
    }
    const closeOrbyLink = () => {
        updateState({ visible: false });
        options.onExit?.();
    };
    const linkAccount = (accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.READ, OrbyLinkOperation.LINK_ACCOUNT, accountClusterId, sessionId, customIframeId);
    };
    const deposit = (paymentInfo, accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.WRITE, OrbyLinkOperation.DEPOSIT, accountClusterId, sessionId, customIframeId, undefined, paymentInfo);
    };
    const payWithCrypto = (paymentInfo, accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.WRITE, OrbyLinkOperation.PAY_WITH_CRYPTO, accountClusterId, sessionId, customIframeId, undefined, paymentInfo);
    };
    const openOrbyLink = (permissions, operation, accountClusterId, sessionId, _customIframeId, platformId, paymentInfo) => {
        // Generate sessionId if not provided
        let finalSessionId = sessionId;
        if (!finalSessionId) {
            finalSessionId = generateUUID();
        }
        else if (!isValidUUID(finalSessionId)) {
            options?.onExit?.("Invalid sessionId format! Must be a valid UUID.");
            return;
        }
        // Build link URL pointing to oauth-backend
        const baseUrl = options.baseUrl.replace(/\/$/, ""); // Remove trailing slash
        // In React Native, we don't have window.location.origin
        // We'll use a placeholder or get it from the app's configuration
        const parentOrigin = "react-native://app";
        // Build query parameters, only including those that are defined
        const params = new URLSearchParams();
        params.set("sessionId", finalSessionId);
        params.set("parentOrigin", parentOrigin);
        params.set("operation", operation);
        params.set("styles", JSON.stringify({
            backgroundColor: styles.bottomSheet.backgroundColor,
        }));
        if (accountClusterId) {
            params.set("accountClusterId", accountClusterId);
        }
        if (paymentInfo) {
            params.set("paymentTokenInfo", JSON.stringify(paymentInfo));
        }
        if (permissions) {
            params.set("permissions", permissions);
        }
        if (platformId) {
            params.set("platformId", platformId);
        }
        const linkUrl = `${baseUrl}/link?${params.toString()}`;
        // Add language if specified
        const finalLinkUrl = addLanguage(linkUrl, options?.language);
        updateState({
            visible: true,
            url: finalLinkUrl,
            options,
        });
    };
    return { openOrbyLink, closeOrbyLink, linkAccount, deposit, payWithCrypto };
};
